import { apiClient } from '@/api/client';
import { uploadDirectMany } from '@/api/direct-upload.api';

export type PhotoTag =
  | 'x-ray'
  | 'clinical'
  | 'post-operative'
  | 'before-treatment'
  | 'after-treatment'
  | 'lab-report'
  | 'medical-report'
  | 'referral'
  | 'consent'
  | 'insurance'
  | 'other';

/** image: photos and X-rays. document: PDFs. */
export type PatientFileKind = 'image' | 'document';

export interface Photo {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  tag: PhotoTag;
  description?: string;
  patientId?: string;
  reservationId?: string;
  /** When the visit the photo belongs to was scheduled; null without one. */
  visitDate?: string | null;
  signedUrl: string;
  createdAt: string;
}

export interface GetUploadUrlParams {
  fileName: string;
  mimeType: string;
  tag?: PhotoTag;
  description?: string;
  patientId?: string;
  reservationId?: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  photoId: string;
}

export interface PhotoFilters {
  patientId?: string;
  reservationId?: string;
  tag?: PhotoTag;
  kind?: PatientFileKind;
  page?: number;
  limit?: number;
}

export interface ClaimPatientFileParams {
  uploadKey: string;
  tag: PhotoTag;
  description?: string;
  patientId?: string;
  reservationId?: string;
}

export interface PaginatedPhotos {
  data: Photo[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type AnnotationType = 'pen' | 'arrow' | 'text' | 'eraser' | 'move';

export interface PenData {
  points: { x: number; y: number }[];
}
export interface ArrowData {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
export interface TextData {
  x: number;
  y: number;
  text: string;
  fontSize: number;
}

export interface PhotoAnnotation {
  id: string;
  photoId: string;
  type: AnnotationType;
  data: PenData | ArrowData | TextData;
  color: string;
  authorId: string | null;
  authorName: string | null;
  createdAt: string;
}

export interface PhotoComment {
  id: string;
  photoId: string;
  content: string;
  authorId: string | null;
  authorName: string | null;
  createdAt: string;
  updatedAt: string;
}

export const photosApi = {
  // Get presigned upload URL
  getUploadUrl: async (params: GetUploadUrlParams) => {
    const response = await apiClient.post<UploadUrlResponse>(
      `/photos/upload-url`,
      params,
    );
    return response.data;
  },

  // Confirm upload completion
  confirmUpload: async (photoId: string, s3Key: string, fileSize: number) => {
    const response = await apiClient.post<{
      success: boolean;
      photoId: string;
    }>(`/photos/confirm/${photoId}`, { s3Key, fileSize });
    return response.data;
  },

  // Get photos with filters and pagination
  getPhotos: async (filters?: PhotoFilters): Promise<PaginatedPhotos> => {
    const params = new URLSearchParams();
    if (filters?.patientId) params.append('patientId', filters.patientId);
    if (filters?.reservationId)
      params.append('reservationId', filters.reservationId);
    if (filters?.tag) params.append('tag', filters.tag);
    if (filters?.kind) params.append('kind', filters.kind);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get<PaginatedPhotos>(
      `/photos?${params.toString()}`,
    );
    return response.data;
  },

  /** Files an upload from `uploadDirect(file, 'patient-file')`. */
  claimFile: async (params: ClaimPatientFileParams) => {
    const response = await apiClient.post<Photo>('/photos/files', params);
    return response.data;
  },

  // Get single photo
  getPhoto: async (photoId: string) => {
    const response = await apiClient.get<Photo>(`/photos/${photoId}`);
    return response.data;
  },

  // Delete photo
  deletePhoto: async (photoId: string) => {
    const response = await apiClient.delete<{ success: boolean }>(
      `/photos/${photoId}`,
    );
    return response.data;
  },

  xrayImport: async (
    files: File[],
    sensorCode: string,
    capturedAt: string,
    reservationId?: string,
  ) => {
    const uploadKeys = await uploadDirectMany(files, 'xray');
    const response = await apiClient.post('/photos/xray-import', {
      sensorCode,
      capturedAt,
      reservationId: reservationId || undefined,
      uploadKeys,
    });
    return response.data as {
      uploaded: number;
      patientId: string;
      patientName: string;
      reservationId: string | null;
      warning?: 'no_reservation';
    };
  },

  // Get comments for a photo
  getComments: async (photoId: string): Promise<PhotoComment[]> => {
    const response = await apiClient.get<PhotoComment[]>(
      `/photos/${photoId}/comments`,
    );
    return response.data;
  },

  // Add a comment to a photo
  addComment: async (
    photoId: string,
    content: string,
  ): Promise<PhotoComment> => {
    const response = await apiClient.post<PhotoComment>(
      `/photos/${photoId}/comments`,
      { content },
    );
    return response.data;
  },

  // Delete a photo comment
  deleteComment: async (commentId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(
      `/photos/comments/${commentId}`,
    );
    return response.data;
  },

  // Get annotations for a photo
  getAnnotations: async (photoId: string): Promise<PhotoAnnotation[]> => {
    const response = await apiClient.get<PhotoAnnotation[]>(
      `/photos/${photoId}/annotations`,
    );
    return response.data;
  },

  // Save an annotation
  addAnnotation: async (
    photoId: string,
    type: AnnotationType,
    data: PenData | ArrowData | TextData,
    color: string,
  ): Promise<PhotoAnnotation> => {
    const response = await apiClient.post<PhotoAnnotation>(
      `/photos/${photoId}/annotations`,
      { type, data, color },
    );
    return response.data;
  },

  // Delete an annotation
  deleteAnnotation: async (
    annotationId: string,
  ): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(
      `/photos/annotations/${annotationId}`,
    );
    return response.data;
  },

  // Update annotation position/data (e.g. when moved)
  updateAnnotation: async (
    annotationId: string,
    data: PenData | ArrowData | TextData,
  ): Promise<PhotoAnnotation> => {
    const response = await apiClient.patch<PhotoAnnotation>(
      `/photos/annotations/${annotationId}`,
      { data },
    );
    return response.data;
  },

  // Upload file to S3 using presigned URL
  uploadToS3: async (uploadUrl: string, file: File) => {
    console.log('Uploading to S3:', {
      url: uploadUrl,
      fileType: file.type,
      fileSize: file.size,
    });
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    console.log('S3 upload response:', {
      status: response.status,
      ok: response.ok,
    });
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('S3 upload error:', errorText);
      throw new Error(
        `S3 upload failed: ${response.status} ${response.statusText}`,
      );
    }
  },
};
